import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Box,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Visibility,
  Print,
  CheckCircle,
  Pending,
  Cancel,
  Assessment,
  Edit,
  Save,
  Close,
  Download,
  ShoppingCart
} from '@mui/icons-material';
import { COLORS } from '../../theme/colors';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminQuotes, updateQuoteStatus } from '../../services/quoteService';

const AdminQuotes = () => {
  const { user, userData } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteDetailDialog, setQuoteDetailDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingQuote, setEditingQuote] = useState(null);
  const [updateData, setUpdateData] = useState({
    status: '',
    adminNotes: '',
    finalQuote: ''
  });

  useEffect(() => {
    if (user && userData?.role === 'admin') {
      fetchQuotes();
    }
  }, [user, userData, statusFilter]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const adminQuotes = await getAdminQuotes(statusFilter, 100, 0);
      setQuotes(adminQuotes);
    } catch (error) {
      console.error('Error fetching admin quotes:', error);
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const viewQuoteDetails = (quote) => {
    setSelectedQuote(quote);
    setQuoteDetailDialog(true);
  };

  const startEditing = (quote) => {
    setEditingQuote(quote.id);
    setUpdateData({
      status: quote.status,
      adminNotes: quote.adminNotes || '',
      finalQuote: quote.finalQuote || ''
    });
  };

  const cancelEditing = () => {
    setEditingQuote(null);
    setUpdateData({
      status: '',
      adminNotes: '',
      finalQuote: ''
    });
  };

  const saveQuoteUpdate = async (quoteId) => {
    try {
      const updatePayload = {
        status: updateData.status,
        adminNotes: updateData.adminNotes
      };

      if (updateData.finalQuote && !isNaN(parseFloat(updateData.finalQuote))) {
        updatePayload.finalQuote = parseFloat(updateData.finalQuote);
      }

      await updateQuoteStatus(quoteId, updatePayload);
      
      // Update the quote in the local state
      setQuotes(quotes.map(quote => 
        quote.id === quoteId 
          ? { ...quote, ...updatePayload, updatedAt: new Date() }
          : quote
      ));

      setEditingQuote(null);
      setUpdateData({
        status: '',
        adminNotes: '',
        finalQuote: ''
      });
    } catch (error) {
      console.error('Error updating quote:', error);
      setError('Failed to update quote');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in-review':
        return 'info';
      case 'quoted':
        return 'primary';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Pending />;
      case 'in-review':
        return <Assessment />;
      case 'quoted':
        return <Print />;
      case 'accepted':
      case 'completed':
        return <CheckCircle />;
      case 'rejected':
        return <Cancel />;
      default:
        return <Pending />;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      
      // Handle Firestore Timestamp object
      if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      }
      // Handle Firestore Timestamp with _seconds/_nanoseconds (serialized format)
      else if (timestamp && typeof timestamp === 'object' && timestamp._seconds) {
        date = new Date(timestamp._seconds * 1000);
      }
      // Handle Firestore Timestamp with seconds/nanoseconds
      else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      }
      // Handle timestamp as string or number
      else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
        date = new Date(timestamp);
      }
      // Handle already parsed Date object
      else if (timestamp instanceof Date) {
        date = timestamp;
      }
      else {
        date = new Date(timestamp);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Error parsing date';
    }
  };

  const getQuotesByStatus = () => {
    const statusCounts = {
      all: quotes.length,
      pending: quotes.filter(q => q.status === 'pending').length,
      'in-review': quotes.filter(q => q.status === 'in-review').length,
      quoted: quotes.filter(q => q.status === 'quoted').length,
      accepted: quotes.filter(q => q.status === 'accepted').length,
      rejected: quotes.filter(q => q.status === 'rejected').length,
      completed: quotes.filter(q => q.status === 'completed').length
    };
    return statusCounts;
  };

  if (!user || userData?.role !== 'admin') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Access denied. Admin privileges required.
        </Alert>
      </Container>
    );
  }

  const statusCounts = getQuotesByStatus();

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} mb={4} color={COLORS.text.primary}>
        Quote Management
      </Typography>

      {/* Status Filter Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={statusFilter}
          onChange={(e, newValue) => setStatusFilter(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={`All (${statusCounts.all})`} value="all" />
          <Tab label={`Pending (${statusCounts.pending})`} value="pending" />
          <Tab label={`In Review (${statusCounts['in-review']})`} value="in-review" />
          <Tab label={`Quoted (${statusCounts.quoted})`} value="quoted" />
          <Tab label={`Accepted (${statusCounts.accepted})`} value="accepted" />
          <Tab label={`Completed (${statusCounts.completed})`} value="completed" />
          <Tab label={`Rejected (${statusCounts.rejected})`} value="rejected" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : quotes.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No quote requests found
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>File Name</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Material</strong></TableCell>
                <TableCell><strong>Estimated</strong></TableCell>
                <TableCell><strong>Final Quote</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Submitted</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {quotes.map((quote) => (
                <TableRow key={quote.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {quote.fileName || 'Unknown File'}
                    </Typography>
                    {quote.modelAnalysis?.volume && (
                      <Typography variant="caption" color="text.secondary">
                        {quote.modelAnalysis.volume.toFixed(1)} cm³
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {quote.userName || quote.userEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {quote.userEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {quote.printSettings?.material || 'N/A'}
                  </TableCell>
                  <TableCell>
                    ₹{quote.quote?.total?.toFixed(2) || 'N/A'}
                  </TableCell>
                  <TableCell>
                    {editingQuote === quote.id ? (
                      <TextField
                        size="small"
                        type="number"
                        value={updateData.finalQuote}
                        onChange={(e) => setUpdateData({
                          ...updateData,
                          finalQuote: e.target.value
                        })}
                        placeholder="Final quote"
                        sx={{ width: 100 }}
                      />
                    ) : (
                      quote.finalQuote ? `₹${quote.finalQuote.toFixed(2)}` : '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {editingQuote === quote.id ? (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={updateData.status}
                          onChange={(e) => setUpdateData({
                            ...updateData,
                            status: e.target.value
                          })}
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="in-review">In Review</MenuItem>
                          <MenuItem value="quoted">Quoted</MenuItem>
                          <MenuItem value="accepted">Accepted</MenuItem>
                          <MenuItem value="rejected">Rejected</MenuItem>
                          <MenuItem value="completed">Completed</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        icon={getStatusIcon(quote.status)}
                        label={quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                        color={getStatusColor(quote.status)}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(quote.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => viewQuoteDetails(quote)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      
                      {quote.orderId && (
                        <Tooltip title="View Order">
                          <IconButton
                            size="small"
                            color="success"
                            component="a"
                            href={`/admin/orders?search=${quote.orderId}`}
                            target="_blank"
                          >
                            <ShoppingCart />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      {editingQuote === quote.id ? (
                        <>
                          <Tooltip title="Save">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => saveQuoteUpdate(quote.id)}
                            >
                              <Save />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Cancel">
                            <IconButton
                              size="small"
                              onClick={cancelEditing}
                            >
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => startEditing(quote)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}

                      {quote.modelUrl && (
                        <Tooltip title="Download STL">
                          <IconButton
                            size="small"
                            component="a"
                            href={quote.modelUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Quote Detail Dialog */}
      <Dialog
        open={quoteDetailDialog}
        onClose={() => setQuoteDetailDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedQuote && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Quote Details - {selectedQuote.fileName}</Typography>
                <Chip
                  icon={getStatusIcon(selectedQuote.status)}
                  label={selectedQuote.status.charAt(0).toUpperCase() + selectedQuote.status.slice(1)}
                  color={getStatusColor(selectedQuote.status)}
                />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                {/* Customer Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Customer Information
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Name:</strong> {selectedQuote.userName || 'N/A'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Email:</strong> {selectedQuote.userEmail}
                  </Typography>
                  {selectedQuote.orderId && (
                    <Typography variant="body2" mb={1}>
                      <strong>Order ID:</strong> {selectedQuote.orderId}
                    </Typography>
                  )}
                </Grid>

                {/* File Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    File Information
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>File Name:</strong> {selectedQuote.fileName}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Dimensions:</strong> {selectedQuote.modelAnalysis?.boundingBox ? 
                      `${selectedQuote.modelAnalysis.boundingBox.x.toFixed(1)} × ${selectedQuote.modelAnalysis.boundingBox.y.toFixed(1)} × ${selectedQuote.modelAnalysis.boundingBox.z.toFixed(1)} mm` : 'N/A'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Volume:</strong> {selectedQuote.modelAnalysis?.volume?.toFixed(2) || 'N/A'} cm³
                  </Typography>
                  {selectedQuote.modelUrl && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Download />}
                      component="a"
                      href={selectedQuote.modelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mt: 1 }}
                    >
                      Download STL
                    </Button>
                  )}
                </Grid>

                {/* Print Settings */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Print Settings
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Material:</strong> {selectedQuote.printSettings?.material}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Layer Height:</strong> {selectedQuote.printSettings?.layerHeight}mm
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Infill:</strong> {selectedQuote.printSettings?.infill}%
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Supports:</strong> {selectedQuote.printSettings?.supports ? 'Yes' : 'No'}
                  </Typography>
                </Grid>

                {/* Cost Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600} mb={1}>
                    Cost Information
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Estimated Total:</strong> ₹{selectedQuote.quote?.total?.toFixed(2) || 'N/A'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Print Time:</strong> {selectedQuote.quote?.printTime?.toFixed(1) || 'N/A'} hours
                  </Typography>
                  {selectedQuote.finalQuote && (
                    <Typography variant="body2" mb={1} color="primary">
                      <strong>Final Quote:</strong> ₹{selectedQuote.finalQuote.toFixed(2)}
                    </Typography>
                  )}
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  {selectedQuote.customerNotes && (
                    <>
                      <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Customer Notes:
                      </Typography>
                      <Typography variant="body2" mb={2}>
                        {selectedQuote.customerNotes}
                      </Typography>
                    </>
                  )}
                  
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Admin Notes:
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={selectedQuote.adminNotes || ''}
                    onChange={(e) => setSelectedQuote({
                      ...selectedQuote,
                      adminNotes: e.target.value
                    })}
                    placeholder="Add admin notes..."
                  />
                </Grid>

                {/* Status Update */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Update Status:
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={selectedQuote.status}
                      onChange={(e) => setSelectedQuote({
                        ...selectedQuote,
                        status: e.target.value
                      })}
                    >
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="in-review">In Review</MenuItem>
                      <MenuItem value="quoted">Quoted</MenuItem>
                      <MenuItem value="accepted">Accepted</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Final Quote */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} mb={1}>
                    Final Quote (₹):
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    value={selectedQuote.finalQuote || ''}
                    onChange={(e) => setSelectedQuote({
                      ...selectedQuote,
                      finalQuote: parseFloat(e.target.value) || null
                    })}
                    placeholder="Enter final quote amount"
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setQuoteDetailDialog(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    const updatePayload = {
                      status: selectedQuote.status,
                      adminNotes: selectedQuote.adminNotes || '',
                    };
                    
                    if (selectedQuote.finalQuote) {
                      updatePayload.finalQuote = selectedQuote.finalQuote;
                    }

                    await updateQuoteStatus(selectedQuote.id, updatePayload);
                    
                    // Update quotes list
                    setQuotes(quotes.map(quote => 
                      quote.id === selectedQuote.id 
                        ? { ...quote, ...updatePayload, updatedAt: new Date() }
                        : quote
                    ));
                    
                    setQuoteDetailDialog(false);
                  } catch (error) {
                    setError('Failed to update quote');
                  }
                }}
                sx={{ backgroundColor: COLORS.primary.main }}
              >
                Update Quote
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default AdminQuotes;
